export const CURRENCIES = [
  { code: 'EUR', symbol: '€' },
  { code: 'USD', symbol: '$' },
  { code: 'GBP', symbol: '£' },
  { code: 'JPY', symbol: '¥' },
  { code: 'CHF', symbol: 'CHF' },
  { code: 'SEK', symbol: 'kr' },
  { code: 'NOK', symbol: 'kr' },
  { code: 'DKK', symbol: 'kr' },
  { code: 'PLN', symbol: 'zł' },
  { code: 'CZK', symbol: 'Kč' },
  { code: 'RSD', symbol: 'дин' },
  { code: 'AUD', symbol: '$' },
  { code: 'CAD', symbol: '$' },
]

const cache = new Map()
function formatter(currency, opts, locale) {
  const key = `${locale ?? ''}:${currency}:${JSON.stringify(opts)}`
  let f = cache.get(key)
  if (!f) {
    f = new Intl.NumberFormat(locale, { style: 'currency', currency, ...opts })
    cache.set(key, f)
  }
  return f
}

/**
 * @param {number} amount
 * @param {object} o
 * @param {boolean} o.signed  keep the leading + on positive values
 * @param {boolean} o.round   drop the decimals (for big headline figures)
 * @param {boolean} o.compact shorten to 1.6M / 82K (for axis ticks)
 */
export function formatMoney(
  amount,
  { currency = 'EUR', signed = false, round = false, compact = false, locale } = {},
) {
  const n = Number.isFinite(amount) ? amount : 0
  const digits = round && !compact ? 0 : undefined
  const out = formatter(
    currency,
    {
      minimumFractionDigits: digits,
      maximumFractionDigits: compact ? 1 : digits,
      ...(compact ? { notation: 'compact' } : {}),
    },
    locale,
  ).format(round && !compact ? Math.round(n) : n)
  return signed && n > 0 ? `+${out}` : out
}

export function currencySymbol(currency = 'EUR') {
  const parts = formatter(currency, {}).formatToParts(0)
  return parts.find((p) => p.type === 'currency')?.value ?? currency
}

/**
 * Parses whatever the user types: "1.234,56", "1,234.56", "12 €", "-40".
 * Returns NaN when there is no number in there at all.
 */
export function parseAmount(input) {
  if (typeof input === 'number') return input
  let s = String(input ?? '').trim()
  if (!s) return NaN
  const negative = /^-/.test(s) || /\(.*\)/.test(s)
  s = s.replace(/[^\d.,]/g, '')
  if (!s) return NaN
  const lastComma = s.lastIndexOf(',')
  const lastDot = s.lastIndexOf('.')
  const decimalAt = Math.max(lastComma, lastDot)
  // A separator followed by exactly 1-2 digits at the end is a decimal point;
  // anything else is thousands grouping.
  const isDecimal = decimalAt > -1 && s.length - decimalAt - 1 <= 2 && s.length - decimalAt - 1 > 0
  const intPart = (isDecimal ? s.slice(0, decimalAt) : s).replace(/[.,]/g, '')
  const fracPart = isDecimal ? s.slice(decimalAt + 1) : ''
  const value = Number(`${intPart || '0'}.${fracPart || '0'}`)
  if (!Number.isFinite(value)) return NaN
  return negative ? -value : value
}

export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100
